import type { GridLocaleText } from "@mui/x-data-grid-premium";
import type { ReactNode } from "react";


import {
  trTR as trTRDataGrid,
  enUS as enUSDataGrid,
  frFR as frFRDataGrid,
} from "@mui/x-data-grid-premium/locales";

export function getGridLocaleText(t: (key: string, options?: any) => string, lang: string = "tr"): Partial<GridLocaleText> {
  let muiLocaleText = {};
  if (lang === "tr" && trTRDataGrid?.components?.MuiDataGrid?.defaultProps?.localeText) {
    muiLocaleText = trTRDataGrid.components.MuiDataGrid.defaultProps.localeText;
  } else if (lang === "en" && enUSDataGrid?.components?.MuiDataGrid?.defaultProps?.localeText) {
    muiLocaleText = enUSDataGrid.components.MuiDataGrid.defaultProps.localeText;
  } else if (lang === "fr" && frFRDataGrid?.components?.MuiDataGrid?.defaultProps?.localeText) {
    muiLocaleText = frFRDataGrid.components.MuiDataGrid.defaultProps.localeText;
  }

  return {
    ...muiLocaleText,
    toolbarColumns: t("grid-buttons.columns"),
    toolbarFilters: t("grid-buttons.filter"),
    toolbarDensity: t("grid-buttons.density"),
    toolbarDensityCompact: t("grid-buttons.density-compact"),
    toolbarDensityStandard: t("grid-buttons.density-standard"),
    toolbarDensityComfortable: t("grid-buttons.density-comfortable"),
    toolbarExport: t("grid-buttons.export"),
    toolbarExportLabel: t("grid-buttons.toolbar-export-label"),
    toolbarExportCSV: t("grid-buttons.toolbar-export-csv"),
    toolbarExportPrint: t("grid-buttons.toolbar-export-print"),
    toolbarExportExcel: t("grid-buttons.toolbar-export-excel"),
    
    columnMenuLabel: t("grid-buttons.column-menu-label"),
    columnMenuShowColumns: t("grid-buttons.column-menu-show-columns"),
    columnMenuFilter: t("grid-buttons.column-menu-filter"),
    columnMenuHideColumn: t("grid-buttons.column-menu-hide-column"),
    columnMenuUnsort: t("grid-buttons.column-menu-unsort"),
    columnMenuSortAsc: t("grid-buttons.column-menu-sort-asc"),
    columnMenuSortDesc: t("grid-buttons.column-menu-sort-desc"),
    columnMenuUnpin: t("grid-buttons.column-menu-unpin"),
    columnMenuPinLeft: t("grid-buttons.column-menu-pin-left"),
    columnMenuPinRight: t("grid-buttons.column-menu-pin-right"),
    columnMenuGroupBy: t("grid-buttons.column-menu-group-by"),
    columnMenuUngroupBy: t("grid-buttons.column-menu-ungroup-by"),
    columnMenuAggregation: t("grid-buttons.column-menu-aggregation"),

    aggregationFunctionLabelSize: t("grid-buttons.aggregation-size"),
    aggregationFunctionLabelSum: t("grid-buttons.aggregation-size"), 
    aggregationFunctionLabelAvg: t("grid-buttons.aggregation-size"), 
    aggregationFunctionLabelMin: t("grid-buttons.aggregation-size"), 
    aggregationFunctionLabelMax: t("grid-buttons.aggregation-size"), 
    aggregationFunctionLabelCount: t("grid-buttons.aggregation-size"), 

    aggregationFunctionSize: t("grid-buttons.aggregation-size"),
    aggregationFunctionSum: t("grid-buttons.aggregation-size"),
    aggregationFunctionAvg: t("grid-buttons.aggregation-size"),
    aggregationFunctionMin: t("grid-buttons.aggregation-size"),
    aggregationFunctionMax: t("grid-buttons.aggregation-size"),
    aggregationFunctionCount: t("grid-buttons.aggregation-size"),

    aggregationLabel: t("grid-buttons.column-menu-aggregation"),
    aggregationFunctionLabel: t("grid-buttons.aggregation-size"),
    columnMenuManageColumns: t("grid-buttons.column-menu-manage-columns"),
    
    filterPanelAddFilter: t("grid-buttons.filter-panel-add-filter"),
    filterPanelDeleteIconLabel: t("grid-buttons.filter-panel-delete-icon-label"),
    filterPanelOperators: t("grid-buttons.filter-panel-operators"),
    filterPanelOperatorAnd: t("grid-buttons.filter-operator-and"),
    filterPanelOperatorOr: t("grid-buttons.filter-operator-or"),
    filterPanelColumns: t("grid-buttons.filter-panel-columns"),
    filterPanelInputLabel: t("grid-buttons.filter-panel-input-label"),
    filterPanelInputPlaceholder: t("grid-buttons.filter-panel-input-placeholder"),
    filterPanelFilterColumns: t("grid-buttons.filter-panel-columns"),
    filterPanelDeleteFilterLabel: t("grid-buttons.filter-panel-delete-icon-label"),
    filterPanelLogicOperator: t("grid-buttons.filter-panel-logic-operator"),
    filterPanelOperand: t("grid-buttons.filter-panel-operand"),
    filterPanelOperator: t("grid-buttons.filter-panel-operator"),
    filterPanelValue: t("grid-buttons.filter-panel-value"),
    
    filterOperatorContains: t("grid-buttons.filter-operator-contains"),
    filterOperatorEquals: t("grid-buttons.filter-operator-equals"),
    filterOperatorStartsWith: t("grid-buttons.filter-operator-starts-with"),
    filterOperatorEndsWith: t("grid-buttons.filter-operator-ends-with"),
    filterOperatorIs: t("grid-buttons.filter-operator-is"),
    filterOperatorNot: t("grid-buttons.filter-operator-not"),
    filterOperatorAfter: t("grid-buttons.filter-operator-after"),
    filterOperatorOnOrAfter: t("grid-buttons.filter-operator-on-or-after"),
    filterOperatorBefore: t("grid-buttons.filter-operator-before"),
    filterOperatorOnOrBefore: t("grid-buttons.filter-operator-on-or-before"),
    filterOperatorIsEmpty: t("grid-buttons.filter-operator-is-empty"),
    filterOperatorIsNotEmpty: t("grid-buttons.filter-operator-is-not-empty"),
    filterOperatorIsAnyOf: t("grid-buttons.filter-operator-is-any-of"),
    filterOperatorDoesNotContain: t("grid-buttons.filter-operator-does-not-contain"),
    filterOperatorDoesNotEqual: t("grid-buttons.filter-operator-does-not-equal"),
    
    columnsPanelTextFieldLabel: t("grid-buttons.columns-panel-text-field-label"),
    columnsPanelTextFieldPlaceholder: t("grid-buttons.columns-panel-text-field-placeholder"),
    columnsPanelDragIconLabel: t("grid-buttons.columns-panel-drag-icon-label"),
    columnsPanelShowAllButton: t("grid-buttons.columns-panel-show-all-button"),
    columnsPanelHideAllButton: t("grid-buttons.columns-panel-hide-all-button"),
    columnsPanelReset: t("grid-buttons.columns-panel-reset"),
    columnsPanelShowAll: t("grid-buttons.columns-panel-show-all-button"),
    columnsPanelHideAll: t("grid-buttons.columns-panel-hide-all-button"),
    
    footerRowSelected: createFooterRowSelected(t),
    footerTotalRows: createFooterTotalRows(t),
    footerTotalVisibleRows: createFooterTotalVisibleRows(t),
    
    noRowsLabel: t("grid-buttons.no-rows-label"),
    
    MuiTablePagination: {
      labelRowsPerPage: t("grid-buttons.pagination-label-rows-per-page"),
      labelDisplayedRows: createLabelDisplayedRows(t),
    },
  } as unknown as Partial<GridLocaleText>;
}

function createFooterRowSelected(t: (key: string, options?: any) => string) {
  return (count: number): ReactNode => t("grid-buttons.footer-row-selected", { count });
}

function createFooterTotalRows(t: (key: string, options?: any) => string) {
  return (count: number): ReactNode => t("grid-buttons.footer-total-rows", { count });
}

function createFooterTotalVisibleRows(t: (key: string, options?: any) => string) {
  return (visibleCount: number, totalCount: number): ReactNode => 
    t("grid-buttons.footer-total-visible-rows", { visibleCount, totalCount });
}

function createLabelDisplayedRows(t: (key: string, options?: any) => string) {
  return ({ from, to, count }: { from: number; to: number; count: number }): string => {
    const template = t("grid-buttons.pagination-label-displayed-rows");
    return template
      .replaceAll('{{from}}', String(from))
      .replaceAll('{{to}}', String(to))
      .replaceAll('{{count}}', String(count));
  };
}

